/**
 * Utility functions for time format conversion between HH:MM and decimal hours.
 */

/**
 * Validate HH:MM format string.
 * @param {string} hhmm - String in HH:MM format (e.g., "02:30" or "100:30")
 * @returns {object} - { isValid: boolean, errorMessage: string | null }
 */
export function validateHHMM(hhmm) {
  if (!hhmm || typeof hhmm !== 'string') {
    return { isValid: false, errorMessage: 'Time format is required.' };
  }

  const trimmed = hhmm.trim();

  // Validate format: HH:MM where HH can be any number and MM can be 0-59
  const pattern = /^\d+:[0-5][0-9]$/;
  if (!pattern.test(trimmed)) {
    return {
      isValid: false,
      errorMessage: 'Time must be in HH:MM format (e.g., 02:30 or 100:30). Minutes: 00-59.',
    };
  }

  return { isValid: true, errorMessage: null };
}

/**
 * Convert HH:MM format string to decimal hours.
 * @param {string} hhmm - String in HH:MM format (e.g., "02:30" for 2.5 hours)
 * @returns {number} - Time in decimal hours
 * @throws {Error} - If format is invalid
 */
export function hhmmToDecimal(hhmm) {
  if (!hhmm) {
    throw new Error('Time string cannot be empty');
  }

  const trimmed = hhmm.trim();

  // Validate format
  const validation = validateHHMM(trimmed);
  if (!validation.isValid) {
    throw new Error(validation.errorMessage);
  }

  // Parse hours and minutes
  const parts = trimmed.split(':');
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  // Convert to decimal hours
  const decimalHours = hours + minutes / 60;

  // Round to 2 decimal places
  return Math.round(decimalHours * 100) / 100;
}

/**
 * Convert decimal hours to HH:MM format string.
 * @param {number|string} decimalHours - Decimal or string representing hours (e.g., 2.5 for 2 hours 30 minutes)
 * @returns {string} - Time in HH:MM format (e.g., "02:30")
 * @throws {Error} - If decimalHours is invalid
 */
export function decimalToHHMM(decimalHours) {
  if (decimalHours === null || decimalHours === undefined) {
    return '00:00';
  }

  try {
    // Convert to number
    const num = typeof decimalHours === 'string' ? parseFloat(decimalHours) : decimalHours;

    if (isNaN(num)) {
      throw new Error(`Invalid decimal hours value: ${decimalHours}`);
    }

    // Handle negative values (shouldn't happen, but handle gracefully)
    if (num < 0) {
      return '00:00';
    }

    // Extract hours and minutes
    const totalMinutes = Math.round(num * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    // Format as HH:MM (hours can be any length, minutes always 2 digits)
    return `${hours}:${String(minutes).padStart(2, '0')}`;
  } catch (error) {
    throw new Error(`Invalid decimal hours value: ${decimalHours} - ${error.message}`);
  }
}

/**
 * Format hours and minutes into HH:MM string.
 * @param {number} hours - Hours (0-23)
 * @param {number} minutes - Minutes (0-59)
 * @returns {string} - Time in HH:MM format
 */
export function formatHHMM(hours, minutes) {
  const h = Math.max(0, Math.min(23, Math.floor(hours || 0)));
  const m = Math.max(0, Math.min(59, Math.floor(minutes || 0)));
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

